import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { morph } from '@theme/morph';

const root = window.Shopify?.routes?.root || '/';
const pendingByForm = new Map();
let latestPendingFormId = '';
let processingCartEnhancements = false;

const money = (cents, currency = window.Shopify?.currency?.active || 'USD') => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  } catch {
    return `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
  }
};

function queryFormInput(formId, name) {
  const form = document.getElementById(formId);
  return form?.querySelector(`[name="${name}"]`) || document.querySelector(`[name="${name}"][form="${CSS.escape(formId)}"]`);
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
}

function collectPendingForForm(form) {
  if (!(form instanceof HTMLFormElement) || !form.id) return true;
  const blocks = Array.from(document.querySelectorAll(`commerce-product-block[data-form-id="${CSS.escape(form.id)}"]`));
  const addOns = [];
  let discountCode = '';
  let valid = true;

  blocks.forEach((block) => {
    if (!(block instanceof HTMLElement)) return;
    if (block.hidden || window.getComputedStyle(block).display === 'none') return;
    const requiredGift = block.querySelector('[data-free-gift-selector][data-required="true"]');
    if (requiredGift && !requiredGift.querySelector('[data-commerce-addon]:checked')) {
      requiredGift.classList.add('commerce-validation-error');
      valid = false;
    } else {
      requiredGift?.classList.remove('commerce-validation-error');
    }
    const selectedTier = block.querySelector('[data-quantity-tier]:checked');
    if (selectedTier instanceof HTMLInputElement && selectedTier.dataset.discountCode) {
      discountCode = selectedTier.dataset.discountCode.trim();
    }

    block.querySelectorAll('[data-commerce-addon]:checked').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const variantId = Number(input.dataset.variantId);
      if (!Number.isFinite(variantId) || variantId <= 0) return;
      addOns.push({
        id: variantId,
        quantity: Math.max(1, Number(input.dataset.quantity) || 1),
        properties: {
          '_commerce_addon': input.dataset.addonType || 'add-on',
          '_linked_product': form.dataset.productId || '',
          ...(input.dataset.addonLabel ? { 'Option': input.dataset.addonLabel } : {}),
        },
      });
      if (input.dataset.discountCode) discountCode = input.dataset.discountCode.trim();
    });
  });

  pendingByForm.delete(form.id);
  if (latestPendingFormId === form.id) latestPendingFormId = '';
  if (valid && (addOns.length || discountCode)) {
    pendingByForm.set(form.id, { addOns, discountCode });
    latestPendingFormId = form.id;
  }
  if (!valid) document.querySelector('[data-free-gift-selector].commerce-validation-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return valid;
}

async function applyPendingEnhancements(formId) {
  if (processingCartEnhancements) return;
  const resolvedFormId = pendingByForm.has(formId) ? formId : latestPendingFormId;
  const pending = pendingByForm.get(resolvedFormId);
  if (!pending) return;
  pendingByForm.delete(resolvedFormId);
  if (latestPendingFormId === resolvedFormId) latestPendingFormId = '';
  processingCartEnhancements = true;

  try {
    const sections = cartSectionIds();
    let renderedSections = null;
    let addedAddonKeys = [];
    let discountApplicable = true;

    if (pending.addOns.length) {
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: pending.addOns,
          ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search } : {}),
        }),
      });
      const addData = await addResponse.json();
      if (!addResponse.ok || addData.status) throw new Error(addData.description || addData.message || 'Unable to add option');
      const addedItems = Array.isArray(addData.items) ? addData.items : (addData.key ? [addData] : []);
      addedAddonKeys = addedItems.map((item) => item?.key).filter(Boolean);
      renderedSections = addData.sections || renderedSections;
    }

    if (pending.discountCode) {
      const beforeDiscountResponse = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      const beforeDiscountCart = await beforeDiscountResponse.json();
      if (!beforeDiscountResponse.ok || beforeDiscountCart.status) {
        throw new Error(beforeDiscountCart.description || beforeDiscountCart.message || 'Unable to load cart discounts');
      }

      const normalizeCode = (value) => String(value || '').trim().toLowerCase();
      const existingCodes = Array.isArray(beforeDiscountCart.discount_codes)
        ? beforeDiscountCart.discount_codes
            .filter((discount) => discount?.code && discount?.applicable !== false)
            .map((discount) => String(discount.code))
        : [];
      const requestedCode = String(pending.discountCode || '').trim();
      const mergedCodes = Array.from(
        new Map([...existingCodes, requestedCode].filter(Boolean).map((code) => [normalizeCode(code), code])).values()
      );

      const discountResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: mergedCodes.join(','),
          ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search } : {}),
        }),
      });
      let discountData = await discountResponse.json();
      if (!discountResponse.ok || discountData.status) throw new Error(discountData.description || discountData.message || 'Unable to apply discount');

      const result = Array.isArray(discountData.discount_codes)
        ? discountData.discount_codes.find((discount) => normalizeCode(discount?.code) === normalizeCode(requestedCode))
        : null;
      if (!result || result.applicable !== true) {
        const remainingCodes = Array.isArray(discountData.discount_codes)
          ? discountData.discount_codes
              .filter((discount) => discount?.code && discount?.applicable !== false && normalizeCode(discount.code) !== normalizeCode(requestedCode))
              .map((discount) => String(discount.code))
          : existingCodes;
        const cleanupResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: remainingCodes.join(','),
            ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search } : {}),
          }),
        });
        const cleanedData = await cleanupResponse.json();
        if (cleanupResponse.ok && !cleanedData.status) discountData = cleanedData;
        discountApplicable = false;
        console.warn(`[commerce-product-blocks] Discount code “${requestedCode}” was not applicable and was removed.`);
      }
      renderedSections = discountData.sections || renderedSections;
    }

    const containsGift = pending.addOns.some((item) => item?.properties?._commerce_addon === 'free-gift');
    if (!discountApplicable && containsGift && addedAddonKeys.length) {
      const updates = Object.fromEntries(addedAddonKeys.map((key) => [key, 0]));
      const rollbackResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search } : {}),
        }),
      });
      const rollbackData = await rollbackResponse.json();
      if (rollbackResponse.ok && !rollbackData.status) renderedSections = rollbackData.sections || renderedSections;
    }

    const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    if (renderedSections) cart.sections = renderedSections;

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'product',
        lines: [],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items || [],
        source: 'commerce-product-blocks',
        sourceId: resolvedFormId,
        sections: renderedSections,
        itemCount: cart.item_count || 0,
      },
    });
  } catch (error) {
    console.warn('[commerce-product-blocks] Cart enhancement failed:', error);
  } finally {
    processingCartEnhancements = false;
  }
}


async function publishCommerceCartUpdate(renderedSections = null, sourceId = 'commerce-product-blocks') {
  const cartResponse = await fetch(`${root}cart.js`, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const cart = await cartResponse.json();
  if (renderedSections) cart.sections = renderedSections;
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'product', lines: [], promise: deferred.promise })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items || [],
      source: 'commerce-product-blocks',
      sourceId,
      sections: renderedSections,
      itemCount: cart.item_count || 0,
    },
  });
  return cart;
}

function normalizeExternalVideoUrl(value) {
  try {
    const url = new URL(value, window.location.origin);
    if (url.hostname.includes('youtube.com')) {
      const id = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop();
      return id ? `https://www.youtube.com/embed/${id}` : value;
    }
    if (url.hostname === 'youtu.be') return `https://www.youtube.com/embed/${url.pathname.slice(1)}`;
    if (url.hostname.includes('vimeo.com')) {
      const id = url.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}` : value;
    }
  } catch {}
  return value;
}

class CommerceProductBlock extends HTMLElement {
  abortController;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  initialize() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.applyCountryRule();

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.handleProductSelect, { signal });

    const formId = this.dataset.formId;
    const form = formId ? document.getElementById(formId) : null;
    form?.addEventListener('submit', (event) => {
      if (!collectPendingForForm(form)) event.preventDefault();
    }, { capture: true, signal });

    this.querySelectorAll('[data-commerce-dialog-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.getAttribute('data-commerce-dialog-open');
        const dialog = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
        if (dialog instanceof HTMLDialogElement) dialog.showModal();
      }, { signal });
    });
    this.querySelectorAll('[data-commerce-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close(), { signal });
    });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      }, { signal });
    });

    this.setupGenericDialogsAndChoices();
    switch (this.dataset.commerceKind) {
      case 'delivery-estimate': this.setupDelivery(); break;
      case 'quantity-breaks': this.setupQuantityBreaks(); break;
      case 'size-fit': this.setupSizeFit(); break;
      case 'savings': this.setupSavings(); break;
      case 'countdown': this.setupCountdown(); break;
      case 'restock': this.setupRestock(); break;
      case 'product-faq': this.setupFaq(); break;
      case 'sticky-buy-box': this.setupStickyBuyBox(); break;
      case 'subscription': this.setupSubscription(); break;
      case 'installments': this.setupInstallments(); break;
      case 'use-case': this.setupUseCases(); break;
      case 'compatibility': this.setupCompatibility(); break;
      case 'usage-calculator': this.setupUsageCalculator(); break;
      case 'cost-per-use': this.setupCostPerUse(); break;
      case 'product-videos': this.setupVideoGallery(); break;
      case 'viewer360': this.setupViewer360(); break;
      case 'hotspots': this.setupHotspots(); break;
      case 'before-after': this.setupBeforeAfter(); break;
      case 'product-tabs': this.setupProductTabs(); break;
      case 'loyalty': this.setupLoyalty(); break;
      case 'referral': this.setupReferral(); break;
      case 'wishlist': this.setupWishlist(); break;
      case 'wholesale': this.setupWholesale(); break;
      case 'bulk-order': this.setupBulkOrder(); break;
      default: break;
    }
  }

  applyCountryRule() {
    const rule = (this.dataset.allowedCountries || '').trim();
    if (!rule) return;
    const currentCountry = window.Shopify?.country?.toUpperCase();
    if (!currentCountry) return;
    const allowed = rule.split(',').map((item) => item.trim().toUpperCase()).filter(Boolean);
    this.hidden = allowed.length > 0 && !allowed.includes(currentCountry);
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      const replacement = html.querySelector(`#${CSS.escape(this.id)}`);
      if (!replacement) return;
      const controlState = Array.from(this.querySelectorAll('input, textarea, select')).map((control) => ({
        value: control.value,
        checked: control instanceof HTMLInputElement ? control.checked : undefined,
        selectedIndex: control instanceof HTMLSelectElement ? control.selectedIndex : undefined,
      }));
      morph(this, replacement, { childrenOnly: false });
      Array.from(this.querySelectorAll('input, textarea, select')).forEach((control, index) => {
        const state = controlState[index];
        if (!state) return;
        if (control instanceof HTMLInputElement && typeof state.checked === 'boolean') control.checked = state.checked;
        if (control instanceof HTMLSelectElement && typeof state.selectedIndex === 'number') control.selectedIndex = state.selectedIndex;
        if (!(control instanceof HTMLInputElement && ['radio', 'checkbox'].includes(control.type))) control.value = state.value;
      });
      queueMicrotask(() => this.initialize());
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[commerce-product-block] Variant refresh failed:', error);
    });
  };


  setupGenericDialogsAndChoices() {
    const { signal } = this.abortController;
    this.querySelectorAll('[data-addon-quantity-for]').forEach((quantityInput) => {
      quantityInput.addEventListener('input', () => {
        const choice = quantityInput.closest('.commerce-product-choice');
        const addon = choice?.querySelector('[data-commerce-addon]');
        if (addon) addon.dataset.quantity = String(Math.max(1, Number(quantityInput.value) || 1));
      }, { signal });
    });
    const addonContainer = this.querySelector('[data-addon-products]');
    if (addonContainer?.dataset.allowMultiple === 'false') {
      addonContainer.querySelectorAll('[data-commerce-addon]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          addonContainer.querySelectorAll('[data-commerce-addon]').forEach((other) => {
            if (other !== input) other.checked = false;
          });
        }, { signal });
      });
    }
  }

  setupStickyBuyBox() {
    const { signal } = this.abortController;
    if (this.dataset.stickyDesktop === 'true') {
      this.style.setProperty('--commerce-sticky-top', `${Number(this.dataset.stickyTop) || 0}px`);
      this.classList.add('commerce-product-block--sticky-desktop');
    }
    this.querySelectorAll('[data-commerce-primary-submit]').forEach((button) => {
      button.addEventListener('click', async () => {
        const form = document.getElementById(this.dataset.formId || '');
        if (!(form instanceof HTMLFormElement)) return;
        const quantity = Math.max(1, Number(this.querySelector('[data-commerce-sticky-quantity]')?.value) || 1);
        const quantityInput = queryFormInput(form.id, 'quantity');
        if (quantityInput) quantityInput.value = String(quantity);
        if (!button.hasAttribute('data-buy-now')) {
          form.requestSubmit();
          return;
        }
        button.disabled = true;
        try {
          const formData = new FormData(form);
          formData.set('quantity', String(quantity));
          const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add product');
          window.location.assign(`${root}checkout`);
        } catch (error) {
          console.warn('[commerce-product-blocks] Buy now failed:', error);
          button.disabled = false;
        }
      }, { signal });
    });
  }

  setupSubscription() {
    const { signal } = this.abortController;
    const input = this.querySelector('[data-selling-plan-input]');
    this.querySelectorAll('[data-selling-plan]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked && input instanceof HTMLInputElement) input.value = radio.value;
      }, { signal });
    });
  }

  setupInstallments() {
    const count = Math.max(1, Number(this.dataset.installments) || 1);
    const rate = Math.max(0, Number(this.dataset.annualRate) || 0) / 100;
    const price = Number(this.dataset.priceCents) || 0;
    const estimatedTotal = price * (1 + rate);
    const output = this.querySelector('[data-installment-amount]');
    if (output) output.textContent = `${count} × ${money(estimatedTotal / count)}`;
  }

  setupUseCases() {
    const { signal } = this.abortController;
    const buttons = this.querySelectorAll('[data-use-case-tab]');
    const property = this.querySelector('[data-use-case-property]');
    buttons.forEach((button) => button.addEventListener('click', () => {
      const id = button.dataset.useCaseTab;
      buttons.forEach((item) => item.setAttribute('aria-selected', String(item === button)));
      this.querySelectorAll('[data-use-case-panel]').forEach((panel) => { panel.hidden = panel.dataset.useCasePanel !== id; });
      if (property instanceof HTMLInputElement) property.value = button.textContent.trim();
    }, { signal }));
  }

  setupCompatibility() {
    const { signal } = this.abortController;
    const button = this.querySelector('[data-compat-check]');
    const result = this.querySelector('[data-compat-result]');
    if (!button || !result) return;
    button.addEventListener('click', () => {
      const values = [1, 2, 3].map((index) => (this.querySelector(`[data-compat-field="${index}"]`)?.value || '').trim().toLowerCase());
      let raw = '';
      try {
        raw = JSON.parse(this.querySelector('[data-compat-rules]')?.textContent || '""');
      } catch { raw = this.querySelector('[data-compat-rules]')?.textContent || ''; }
      const rules = String(raw).split(/\r?\n/).map((line) => line.split('|').map((part) => part.trim())).filter((parts) => parts.length >= 4);
      const found = rules.find((parts) => parts.slice(0, 3).every((rule, index) => rule === '*' || rule.toLowerCase() === values[index]));
      result.hidden = false;
      const checker = this.querySelector('[data-compatibility-checker]');
      const ruleMessage = found?.slice(3).join(' | ').trim();
      result.textContent = found ? (ruleMessage || checker?.dataset.matchMessage || 'Compatible.') : (checker?.dataset.noMatchMessage || 'No exact match found.');
    }, { signal });
  }

  setupUsageCalculator() {
    const { signal } = this.abortController;
    const amount = this.querySelector('[data-usage-amount]');
    const coverage = this.querySelector('[data-usage-coverage]');
    const waste = this.querySelector('[data-usage-waste]');
    const output = this.querySelector('[data-usage-result]');
    let recommended = 1;
    const update = () => {
      const required = Math.max(0, Number(amount?.value) || 0);
      const perUnit = Math.max(0.0001, Number(coverage?.value) || 1);
      const extra = Math.max(0, Number(waste?.value) || 0) / 100;
      recommended = Math.max(1, Math.ceil((required * (1 + extra)) / perUnit));
      if (output) output.textContent = String(recommended);
    };
    [amount, coverage, waste].forEach((input) => input?.addEventListener('input', update, { signal }));
    this.querySelector('[data-usage-apply]')?.addEventListener('click', () => {
      const quantityInput = queryFormInput(this.dataset.formId || '', 'quantity');
      if (quantityInput) {
        quantityInput.value = String(recommended);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, { signal });
    update();
  }

  setupCostPerUse() {
    const uses = Math.max(1, Number(this.dataset.expectedUses) || 1);
    const output = this.querySelector('[data-cost-result]');
    if (output) output.textContent = money((Number(this.dataset.priceCents) || 0) / uses);
  }

  setupVideoGallery() {
    const { signal } = this.abortController;
    const stage = this.querySelector('.commerce-video-stage');
    if (!stage) return;
    this.querySelectorAll('[data-video-source]').forEach((button) => button.addEventListener('click', () => {
      const source = button.dataset.videoSource;
      const url = button.dataset.videoUrl;
      if (!url) return;
      stage.replaceChildren();
      if (source === 'hosted') {
        const video = document.createElement('video');
        video.controls = true; video.playsInline = true; video.src = url; video.className = 'commerce-gallery-video';
        stage.append(video);
      } else {
        const iframe = document.createElement('iframe');
        iframe.src = normalizeExternalVideoUrl(url); iframe.allow = 'autoplay; encrypted-media'; iframe.allowFullscreen = true; iframe.className = 'commerce-gallery-video';
        stage.append(iframe);
      }
    }, { signal }));
  }

  setupViewer360() {
    const { signal } = this.abortController;
    const viewer = this.querySelector('[data-viewer360]');
    const stage = this.querySelector('.commerce-viewer360__stage');
    const frames = Array.from(this.querySelectorAll('.commerce-viewer360__frame'));
    if (!stage || frames.length < 2) return;
    let index = 0; let startX = 0; let dragging = false; let timer;
    const show = (next) => {
      index = (next + frames.length) % frames.length;
      frames.forEach((frame, frameIndex) => { frame.hidden = frameIndex !== index; });
    };
    show(0);
    stage.addEventListener('pointerdown', (event) => { dragging = true; startX = event.clientX; stage.setPointerCapture(event.pointerId); }, { signal });
    stage.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) < 12) return;
      show(index + (delta > 0 ? -1 : 1)); startX = event.clientX;
    }, { signal });
    stage.addEventListener('pointerup', () => { dragging = false; }, { signal });
    if (viewer?.dataset.autoplay === 'true') {
      timer = window.setInterval(() => show(index + 1), Math.max(100, Number(viewer.dataset.speed) || 500));
      signal.addEventListener('abort', () => clearInterval(timer), { once: true });
    }
  }

  setupHotspots() {
    const { signal } = this.abortController;
    const buttons = Array.from(this.querySelectorAll('[data-hotspot-button]'));
    const popovers = Array.from(this.querySelectorAll('[data-hotspot-popover]'));
    buttons.forEach((button, index) => button.addEventListener('click', () => {
      popovers.forEach((popover, popoverIndex) => { popover.hidden = popoverIndex !== index || !popover.hidden; });
    }, { signal }));
    popovers.forEach((popover) => { popover.hidden = true; });
  }

  setupBeforeAfter() {
    const { signal } = this.abortController;
    const range = this.querySelector('[data-before-after-range]');
    const clip = this.querySelector('[data-before-after-clip]');
    if (!range || !clip) return;
    const update = () => { clip.style.clipPath = `inset(0 ${100 - Number(range.value)}% 0 0)`; };
    range.addEventListener('input', update, { signal }); update();
  }

  setupProductTabs() {
    const { signal } = this.abortController;
    const buttons = this.querySelectorAll('[data-product-tab]');
    buttons.forEach((button) => button.addEventListener('click', () => {
      const id = button.dataset.productTab;
      buttons.forEach((item) => item.setAttribute('aria-selected', String(item === button)));
      this.querySelectorAll('[data-product-tab-panel]').forEach((panel) => { panel.hidden = panel.dataset.productTabPanel !== id; });
    }, { signal }));
  }

  setupLoyalty() {
    const perUnit = Math.max(0, Number(this.dataset.pointsPerUnit) || 0);
    const multiplier = Math.max(1, Number(this.dataset.pointsMultiplier) || 1);
    const points = Math.floor(((Number(this.dataset.priceCents) || 0) / 100) * perUnit * multiplier);
    const output = this.querySelector('[data-loyalty-result]');
    if (output) output.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en').format(points);
  }

  setupReferral() {
    const { signal } = this.abortController;
    const status = this.querySelector('[data-referral-status]');
    const url = window.location.href;
    this.querySelector('[data-referral-share]')?.addEventListener('click', async () => {
      if (navigator.share) await navigator.share({ title: document.title, url });
      else await navigator.clipboard.writeText(url);
    }, { signal });
    this.querySelector('[data-referral-copy]')?.addEventListener('click', async () => {
      await navigator.clipboard.writeText(url);
      if (status) status.textContent = this.querySelector('[data-referral-reward]')?.dataset.copiedText || 'Product link copied.';
    }, { signal });
  }

  setupWishlist() {
    const { signal } = this.abortController;
    const button = this.querySelector('[data-wishlist-button]');
    if (!button) return;
    const key = 'horizon-commerce-wishlist';
    const productKey = `${this.dataset.productId}:${this.dataset.variantId}`;
    const read = () => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; } };
    const render = () => {
      const saved = read().includes(productKey);
      const label = button.querySelector('[data-wishlist-label]');
      const icon = button.querySelector('[data-wishlist-icon]');
      if (label) label.textContent = saved ? button.dataset.savedLabel : button.dataset.saveLabel;
      if (icon) icon.textContent = saved ? '♥' : '♡';
      button.setAttribute('aria-pressed', String(saved));
    };
    button.addEventListener('click', () => {
      const items = read(); const index = items.indexOf(productKey);
      if (index >= 0) items.splice(index, 1); else items.push(productKey);
      localStorage.setItem(key, JSON.stringify(items)); render();
    }, { signal }); render();
  }

  setupWholesale() {
    const { signal } = this.abortController;
    this.querySelectorAll('[data-wholesale-quantity]').forEach((button) => button.addEventListener('click', () => {
      const quantityInput = queryFormInput(this.dataset.formId || '', 'quantity');
      if (quantityInput) {
        quantityInput.value = String(Math.max(1, Number(button.dataset.wholesaleQuantity) || 1));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, { signal }));
  }

  setupBulkOrder() {
    const { signal } = this.abortController;
    const button = this.querySelector('[data-bulk-add]');
    const status = this.querySelector('[data-bulk-status]');
    button?.addEventListener('click', async () => {
      const items = Array.from(this.querySelectorAll('[data-bulk-variant]')).map((input) => ({ id: Number(input.dataset.bulkVariant), quantity: Math.max(0, Number(input.value) || 0) })).filter((item) => item.id > 0 && item.quantity > 0);
      const wrapper = this.querySelector('[data-bulk-order]');
      if (!items.length) { if (status) status.textContent = wrapper?.dataset.emptyMessage || 'Enter a quantity for at least one variant.'; return; }
      button.disabled = true;
      try {
        const sections = cartSectionIds();
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname + window.location.search } : {}) }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add variants');
        await publishCommerceCartUpdate(data.sections || null, this.dataset.formId || 'bulk-order');
        if (status) status.textContent = wrapper?.dataset.successMessage || 'Selected variants added to cart.';
        this.querySelectorAll('[data-bulk-variant]').forEach((input) => { input.value = '0'; });
      } catch (error) {
        if (status) status.textContent = error.message || 'Unable to add variants.';
      } finally { button.disabled = false; }
    }, { signal });
  }

  setupDelivery() {
    const output = this.querySelector('[data-delivery-output]');
    if (!output) return;
    const processing = Number(this.dataset.processingDays) || 0;
    const minTransit = Number(this.dataset.minTransitDays) || 0;
    const maxTransit = Math.max(minTransit, Number(this.dataset.maxTransitDays) || minTransit);
    const cutoff = Math.min(23, Math.max(0, Number(this.dataset.cutoffHour) || 23));
    const skipWeekends = this.dataset.skipWeekends === 'true';
    const now = new Date();
    const extra = now.getHours() >= cutoff ? 1 : 0;

    const addDays = (start, count) => {
      const result = new Date(start);
      let added = 0;
      while (added < count) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (!skipWeekends || (day !== 0 && day !== 6)) added += 1;
      }
      return result;
    };

    const earliest = addDays(now, processing + minTransit + extra);
    const latest = addDays(now, processing + maxTransit + extra);
    const dateStyle = this.dataset.dateStyle || 'medium';
    const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { month: dateStyle === 'short' ? 'short' : 'long', day: 'numeric', ...(this.dataset.showYear === 'true' ? { year: 'numeric' } : {}) });
    const prefix = this.dataset.deliveryPrefix || '';
    output.textContent = `${prefix}${formatter.format(earliest)} – ${formatter.format(latest)}`;

    const countdown = this.querySelector('[data-cutoff-countdown]');
    if (countdown) {
      const tick = () => {
        const current = new Date();
        const target = new Date(current);
        target.setHours(cutoff, 0, 0, 0);
        if (target <= current) target.setDate(target.getDate() + 1);
        const diff = Math.max(0, target - current);
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        countdown.textContent = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      };
      tick();
      const timer = window.setInterval(tick, 30000);
      this.abortController.signal.addEventListener('abort', () => clearInterval(timer), { once: true });
    }
  }

  setupQuantityBreaks() {
    const radios = Array.from(this.querySelectorAll('[data-quantity-tier]'));
    const formId = this.dataset.formId;
    const update = (radio) => {
      if (!(radio instanceof HTMLInputElement)) return;
      const quantity = Math.max(1, Number(radio.value) || 1);
      const quantityInput = queryFormInput(formId, 'quantity');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = String(quantity);
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const property = this.querySelector('[data-tier-property]');
      if (property instanceof HTMLInputElement) property.value = radio.dataset.tierLabel || `${quantity}`;
    };
    radios.forEach((radio) => radio.addEventListener('change', () => update(radio), { signal: this.abortController.signal }));
    update(radios.find((radio) => radio.checked));
  }

  setupSizeFit() {
    this.querySelectorAll('[data-unit-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const unit = button.dataset.unitToggle;
        this.querySelectorAll('[data-size-value-cm]').forEach((cell) => {
          const cm = Number(cell.dataset.sizeValueCm);
          if (!Number.isFinite(cm)) return;
          const cmLabel = this.dataset.cmLabel || 'cm';
          const inLabel = this.dataset.inLabel || 'in';
          cell.textContent = unit === 'in' ? `${(cm / 2.54).toFixed(1)} ${inLabel}` : `${cm.toFixed(cm % 1 ? 1 : 0)} ${cmLabel}`;
        });
        this.querySelectorAll('[data-unit-toggle]').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      }, { signal: this.abortController.signal });
    });
  }

  setupSavings() {
    const formId = this.dataset.formId;
    const quantityInput = queryFormInput(formId, 'quantity');
    const update = () => {
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      const price = Number(this.dataset.priceCents) || 0;
      const compare = Number(this.dataset.compareCents) || 0;
      const total = price * quantity;
      const compareTotal = compare > price ? compare * quantity : total;
      const saving = Math.max(0, compareTotal - total);
      this.querySelectorAll('[data-saving-current]').forEach((el) => { el.textContent = money(total); });
      this.querySelectorAll('[data-saving-compare]').forEach((el) => { el.textContent = money(compareTotal); });
      this.querySelectorAll('[data-saving-amount]').forEach((el) => { el.textContent = money(saving); });
      this.querySelectorAll('[data-saving-percent]').forEach((el) => { el.textContent = compareTotal > 0 ? `${Math.round((saving / compareTotal) * 100)}%` : '0%'; });
      this.querySelectorAll('[data-saving-quantity]').forEach((el) => { el.textContent = String(quantity); });
    };
    quantityInput?.addEventListener('input', update, { signal: this.abortController.signal });
    quantityInput?.addEventListener('change', update, { signal: this.abortController.signal });
    update();
  }

  setupCountdown() {
    const endValue = this.dataset.countdownEnd;
    if (!endValue) return;
    const end = new Date(endValue);
    if (Number.isNaN(end.getTime())) return;
    const expiredBehavior = this.dataset.expiredBehavior || 'message';
    const tick = () => {
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        if (expiredBehavior === 'hide') this.hidden = true;
        this.querySelectorAll('[data-countdown-value]').forEach((el) => { el.textContent = '00'; });
        const message = this.querySelector('[data-countdown-expired]');
        message?.classList.remove('commerce-hidden');
        if (expiredBehavior === 'disable') {
          const form = document.getElementById(this.dataset.formId || '');
          form?.querySelectorAll('button[type="submit"]').forEach((button) => { button.disabled = true; });
        }
        return;
      }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const values = { days, hours, minutes, seconds };
      Object.entries(values).forEach(([key, value]) => {
        const el = this.querySelector(`[data-countdown-value="${key}"]`);
        if (el) el.textContent = String(value).padStart(2, '0');
      });
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    this.abortController.signal.addEventListener('abort', () => clearInterval(timer), { once: true });
  }

  setupFaq() {
    if (this.dataset.faqAllowMultiple !== 'false') return;
    this.querySelectorAll('details.commerce-details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.querySelectorAll('details.commerce-details').forEach((other) => {
          if (other !== details) other.open = false;
        });
      }, { signal: this.abortController.signal });
    });
  }

  setupRestock() {
    const form = this.querySelector('[data-restock-form]');
    if (!(form instanceof HTMLFormElement) || !form.dataset.endpoint) return;
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button[type="submit"]');
      if (button instanceof HTMLButtonElement) button.disabled = true;
      const message = form.querySelector('[data-restock-message]');
      try {
        const response = await fetch(form.dataset.endpoint, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Request failed');
        if (message) message.textContent = form.dataset.successMessage || 'Thanks. We will notify you.';
        form.reset();
      } catch {
        if (message) message.textContent = form.dataset.errorMessage || 'Please try again.';
      } finally {
        if (button instanceof HTMLButtonElement) button.disabled = false;
      }
    }, { signal: this.abortController.signal });
  }
}

if (!customElements.get('commerce-product-block')) customElements.define('commerce-product-block', CommerceProductBlock);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise?.then(({ detail }) => {
    if (!detail || detail.source === 'commerce-product-blocks' || detail.didError) return;
    if (detail.source === 'product-form-component') applyPendingEnhancements(detail.sourceId || latestPendingFormId);
  }).catch(() => {});
});
